/**
 * FdaLetterDetailPage
 * =============================================================================
 * Standalone page for ``/fda-letter/:letterId`` — the clickthrough destination
 * for FDA warning / untitled letter search results that have no drug linkage
 * (``letter.drug_id === null``). Linked letters continue to use the existing
 * ``/drug/:drugId?source=fda-letter&version=:letterId`` route so the user
 * keeps the drug context.
 *
 * Visual: mirrors the FDA-letter view inside ``DrugDetailPage`` — amber info
 * card with Company/Product/Division/Application metadata + a "Documents" TOC
 * of jump links, then per-section button-driven collapsibles with sanitized
 * HTML (or text fallback). This keeps the unlinked-letter experience visually
 * consistent with the linked-letter one — same fonts, same chrome, same
 * scroll affordance — so users don't see a different-looking page just
 * because we didn't manage to attach the letter to a drug row.
 *
 * Behavior:
 *   - Fetches letter metadata + section summaries on mount via
 *     ``GET /api/fda-letters/documents/:letterId`` (no body text).
 *   - Lazy-loads each section body via
 *     ``GET /api/fda-letters/sections/:section_id`` on first expand and
 *     caches both ``content_html`` and ``content_text``. Re-expanding does
 *     not re-fetch.
 *   - When ``detail.drug_id`` is set, renders a "View drug" link to
 *     ``/drug/:drugId`` so the user can pivot to the drug context. When
 *     ``drug_id`` is null, the link is omitted — the letter stands alone.
 *   - Renders ``DrugReferencesPanel`` when ``detail.drug_references`` is
 *     non-empty so users can navigate from this letter to any drug it
 *     cross-references.
 *
 * Uses ``authApi`` (not raw ``fetch``) so the Firebase ID token is attached
 * automatically — the BFF rejects un-tokened requests to ``/fda-letters/*``
 * with 401. Tests mock ``authApi.getFDALetterDetail`` / ``getFDALetterSection``.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { authApi } from '../services/authApi';
import { sanitizeHtml } from '../utils/sanitizeHtml';
import DrugReferencesPanel from '../components/fdaLetter/DrugReferencesPanel';
import FdaLetterPageViewer from '../components/fdaLetter/FdaLetterPageViewer';
import { usePublishChatContext } from '../shell/chatContext';
import { useTab, findInTab } from '../shell/TabContext';
import type {
  FDALetterDetail,
  FDALetterSectionContent,
} from '../types/fdaLetter';

const FdaLetterDetailPage: React.FC = () => {
  const { letterId } = useParams<{ letterId: string }>();
  const [detail, setDetail] = useState<FDALetterDetail | null>(null);
  const [sectionContent, setSectionContent] = useState<
    Record<number, FDALetterSectionContent>
  >({});
  const [loadingSections, setLoadingSections] = useState<Set<number>>(new Set());
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const inFlightSections = useRef<Set<number>>(new Set());
  const { rootRef } = useTab();

  const numericLetterId = Number(letterId);
  usePublishChatContext(Number.isInteger(numericLetterId) && numericLetterId > 0 ? {
    context: { mode: 'fda_letter', fda_letter: { letter_id: numericLetterId } },
  } : null);

  useEffect(() => {
    if (!letterId) return;
    // Reset state so in-place navigation (param change without remount) does
    // not leave stale error/detail/bodies from the previous letter visible.
    setDetail(null);
    setError(null);
    setSectionContent({});
    setLoadingSections(new Set());
    setExpandedSections(new Set());
    inFlightSections.current = new Set();
    let cancelled = false;
    authApi
      .getFDALetterDetail(Number(letterId))
      .then((data: FDALetterDetail) => {
        if (!cancelled) setDetail(data);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const message =
            e && typeof e === 'object' && 'message' in e
              ? String((e as { message: unknown }).message)
              : String(e);
          setError(message);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [letterId]);

  const loadSectionBody = useCallback(
    async (sectionId: number) => {
      if (sectionContent[sectionId] !== undefined) return;
      if (inFlightSections.current.has(sectionId)) return;
      inFlightSections.current.add(sectionId);
      setLoadingSections((prev) => {
        const next = new Set(prev);
        next.add(sectionId);
        return next;
      });
      try {
        const body: FDALetterSectionContent = await authApi.getFDALetterSection(
          sectionId,
        );
        setSectionContent((prev) => ({ ...prev, [sectionId]: body }));
      } catch {
        // Soft-fail: section stays in "Loading section…" state. The metadata-
        // level error path above handles the user-visible failure case.
      } finally {
        inFlightSections.current.delete(sectionId);
        setLoadingSections((prev) => {
          const next = new Set(prev);
          next.delete(sectionId);
          return next;
        });
      }
    },
    [sectionContent],
  );

  const toggleSection = useCallback(
    (sectionDomId: string, apiSectionId: number) => {
      setExpandedSections((prev) => {
        const next = new Set(prev);
        if (next.has(sectionDomId)) {
          next.delete(sectionDomId);
        } else {
          next.add(sectionDomId);
          // Lazy-fetch on first expand
          void loadSectionBody(apiSectionId);
        }
        return next;
      });
    },
    [loadSectionBody],
  );

  const jumpToSection = useCallback(
    (sectionDomId: string, apiSectionId: number) => {
      setExpandedSections((prev) => {
        if (prev.has(sectionDomId)) return prev;
        const next = new Set(prev);
        next.add(sectionDomId);
        void loadSectionBody(apiSectionId);
        return next;
      });
      setTimeout(() => {
        // Tabs stay mounted, so ids repeat across tabs: look only inside this one.
        const el = findInTab(rootRef, sectionDomId);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    },
    [loadSectionBody, rootRef],
  );

  if (error) {
    return (
      <div className="p-8 text-red-600">
        Error: {error}
      </div>
    );
  }
  if (!detail) {
    return <div className="p-8">Loading…</div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header — letter type pill, issued date, title, View drug link */}
      <header className="mb-4">
        <div className="flex items-center gap-3 mb-2">
          <span
            className={`px-2 py-0.5 text-xs font-semibold uppercase rounded border ${
              detail.letter_type === 'warning'
                ? 'bg-red-50 text-red-700 border-red-200'
                : detail.letter_type === 'promotional'
                ? 'bg-violet-50 text-violet-700 border-violet-200'
                : 'bg-amber-50 text-amber-700 border-amber-200'
            }`}
          >
            {detail.letter_type}
          </span>
          {detail.issued_date && (
            <span className="text-sm text-slate-500">{detail.issued_date}</span>
          )}
        </div>
        <h1 className="text-2xl font-bold text-slate-900">
          {detail.product_name || `FDA Enforcement Letter #${detail.id}`}
        </h1>
        {detail.drug_id !== null && detail.drug_id !== undefined && (
          <Link
            to={`/drug/${detail.drug_id}?source=fda-letter&version=${detail.id}`}
            className="inline-block mt-2 text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
          >
            View drug →
          </Link>
        )}
      </header>

      {/* Info card — amber, mirrors the DrugDetailPage letter view */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <div className="p-6 border-b border-slate-200">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="text-sm text-gray-600 space-y-1">
              {detail.company_name && (
                <div><strong>Company:</strong> {detail.company_name}</div>
              )}
              {detail.product_name && (
                <div><strong>Product:</strong> {detail.product_name}</div>
              )}
              {detail.division && (
                <div><strong>Division:</strong> {detail.division}</div>
              )}
              {detail.application_number && (
                <div><strong>Application:</strong> {detail.application_number}</div>
              )}
            </div>
            {/* Documents TOC — jump to a section + auto-expand */}
            {detail.sections.length > 0 && (
              <div className="mt-3 pt-3 border-t border-amber-200">
                <div className="text-xs font-semibold text-amber-700 uppercase mb-1">
                  Documents
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1">
                  {detail.sections.map((section) => {
                    const sectionDomId = `fda-letter-section-${section.id}`;
                    return (
                      <button
                        key={section.id}
                        onClick={() => jumpToSection(sectionDomId, section.id)}
                        className="text-sm text-indigo-600 hover:text-indigo-800 hover:underline"
                      >
                        {section.title || section.doc_type.replace(/_/g, ' ')}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Cross-reference panel: drugs this letter references */}
        <DrugReferencesPanel
          references={detail.drug_references}
          letterId={detail.id}
        />

        {/* Sections — button-driven collapsibles with prose-styled HTML */}
        {detail.sections.map((section) => {
          const sectionDomId = `fda-letter-section-${section.id}`;
          const isExpanded = expandedSections.has(sectionDomId);
          const cached = sectionContent[section.id];
          const sectionHtml = cached?.content_html;
          const sectionText = cached?.content_text;
          const hasContent =
            section.content_length > 0 || !!(sectionHtml || sectionText);
          const isLoadingContent = loadingSections.has(section.id);
          const contentLoaded = !!(sectionHtml || sectionText);

          return (
            <section
              key={section.id}
              id={sectionDomId}
              className="border-b border-slate-200 last:border-b-0 scroll-mt-20"
            >
              {hasContent && (
                <button
                  onClick={() => toggleSection(sectionDomId, section.id)}
                  className="w-full text-left px-6 py-4 flex items-center gap-3 hover:bg-slate-50 transition-colors"
                >
                  <span
                    className={`text-xs transition-transform ${
                      isExpanded ? 'rotate-90' : ''
                    } text-slate-400`}
                  >
                    &#9654;
                  </span>
                  <h2 className="font-bold tracking-wide text-base uppercase text-slate-900">
                    {section.title || section.doc_type.replace(/_/g, ' ')}
                  </h2>
                </button>
              )}

              {isExpanded && hasContent && (
                <div className="px-6 pb-6 pt-2">
                  {/* Phase 7: page-image viewer for PDF-source sections.
                      The viewer self-fetches /sections/{id}/pages; on an
                      empty pages list (HTML-source) it delegates to the
                      htmlFallback slot below, which is the legacy
                      sanitised-HTML render path.  This standalone page
                      has no chunk-citation handler, so we pass no
                      activeChunkId — only DrugDetailPage's CEPI flow
                      drives bbox highlights today. */}
                  <FdaLetterPageViewer
                    section_id={section.id}
                    htmlFallback={
                      isLoadingContent && !contentLoaded ? (
                        <div className="flex items-center gap-2 py-4 text-sm text-slate-500">
                          <svg
                            className="w-4 h-4 animate-spin"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            />
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                            />
                          </svg>
                          Loading section content...
                        </div>
                      ) : sectionHtml ? (
                        <div
                          className="prose prose-sm max-w-none prose-headings:text-slate-800 prose-p:text-slate-700 prose-li:text-slate-700 prose-td:text-slate-700 prose-th:text-slate-700 [&_table]:text-xs [&_table]:border-collapse [&_th]:bg-slate-50 [&_th]:px-2 [&_th]:py-1 [&_th]:border [&_th]:border-slate-200 [&_td]:px-2 [&_td]:py-1 [&_td]:border [&_td]:border-slate-200"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(sectionHtml),
                          }}
                        />
                      ) : sectionText ? (
                        <div className="prose max-w-none leading-relaxed whitespace-pre-wrap text-sm text-slate-700">
                          {sectionText}
                        </div>
                      ) : null
                    }
                  />
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default FdaLetterDetailPage;
